import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, MapPin, Activity, FolderUp, Settings, LayoutDashboard, LogOut, Trash2, Upload, Search, Shield, Phone, RefreshCw, ExternalLink,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DESTINATIONS, CITIES } from "../data/destinations";

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  ["overview", "Ringkasan", LayoutDashboard], ["users", "Pengguna", Users], ["venues", "Venue & Tempat", MapPin],
  ["activities", "Aktivitas", Activity], ["assets", "Aset", FolderUp], ["settings", "Pengaturan", Settings],
];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtIDR = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : fb; };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#f6f3ee] text-[#2b2521]" data-testid="admin-page">
      <header className="sticky top-0 z-30 border-b border-[#e8e1d8] bg-[#1f1b18] text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-xl kk-coral-gradient"><Shield className="h-4.5 w-4.5" /></span>
            <div>
              <div className="text-[15px] font-extrabold leading-tight">KencanKita · Admin</div>
              <div className="text-[11px] text-white/60" data-testid="admin-email">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="hidden items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-semibold hover:bg-white/20 sm:flex"><ExternalLink className="h-3.5 w-3.5" /> Buka aplikasi</a>
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-semibold hover:bg-white/20"><LogOut className="h-3.5 w-3.5" /> Keluar</button>
          </div>
        </div>
      </header>
      <div className="mx-auto grid max-w-7xl gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[220px_1fr]">
        <nav className="flex gap-1 overflow-x-auto lg:flex-col" data-testid="admin-tabs">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`flex flex-none items-center gap-2 rounded-2xl px-3.5 py-2.5 text-[13px] font-semibold transition-colors ${tab === id ? "bg-[#1f1b18] text-white" : "bg-white text-[#6e594c] hover:bg-[#fff1ec]"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>
        <main className="min-w-0">
          {tab === "overview" && <Overview />}
          {tab === "users" && <UsersTab />}
          {tab === "venues" && <VenuesTab />}
          {tab === "activities" && <ActivitiesTab />}
          {tab === "assets" && <AssetsTab />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

const Card = ({ title, children, action, testId }) => (
  <section className="rounded-3xl border border-[#e8e1d8] bg-white p-5" data-testid={testId}>
    {(title || action) && <div className="mb-4 flex items-center justify-between gap-2"><h2 className="text-[16px] font-bold">{title}</h2>{action}</div>}
    {children}
  </section>
);
const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-[#fbf8f4] p-4" data-testid={testId}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#a69995]">{label}</div>
    <div className="mt-1 text-2xl font-extrabold">{value}</div>
  </div>
);
const Badge = ({ children, tone = "gray" }) => {
  const c = { gray: "bg-[#f1ece6] text-[#6e594c]", green: "bg-[#e3f5ea] text-[#1f7a4a]", red: "bg-[#ffe3e3] text-[#c0343a]", blue: "bg-[#e6f0ff] text-[#2b5fb3]", gold: "bg-[#fff3d6] text-[#9a6b00]" }[tone];
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${c}`}>{children}</span>;
};

function useFetch(path, deps = []) {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const r = await api.get(path); setData(r.data); } catch (e) { toast.error(errMsg(e, "Gagal memuat data")); } finally { setLoading(false); } };
  useEffect(() => { load(); }, deps); // eslint-disable-line
  return { data, loading, reload: load };
}

function Overview() {
  const { data, loading } = useFetch("/admin/overview");
  if (loading || !data) return <div className="p-6 text-[#a69995]">Memuat ringkasan…</div>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Pasangan terdaftar" value={data.couples} testId="stat-couples" />
        <Stat label="Total booking" value={data.bookings} testId="stat-bookings" />
        <Stat label="Kencan selesai" value={data.completed} testId="stat-completed" />
        <Stat label="Aset tersimpan" value={data.assets} testId="stat-assets" />
      </div>
      <Card title="Aktivitas terbaru" testId="overview-recent">
        <ul className="divide-y divide-[#f1ece6]">
          {data.recent.map((a, i) => (
            <li key={i} className="flex items-center justify-between gap-3 py-2.5 text-[13px]">
              <div className="min-w-0"><div className="truncate font-semibold">{a.label}</div><div className="text-[11px] text-[#a69995]">{a.couple} · {fmtDate(a.at)}</div></div>
              <Badge tone={a.type === "booking" ? "blue" : a.status === "credit" ? "green" : "gray"}>{a.type}</Badge>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Kontak admin">
        <div className="flex flex-wrap gap-4 text-[13px]"><span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-[#ff5a5f]" /> {data.admin.email}</span><span className="flex items-center gap-1.5" data-testid="overview-phone"><Phone className="h-4 w-4 text-[#ff5a5f]" /> {data.admin.phone}</span></div>
      </Card>
    </div>
  );
}

function UsersTab() {
  const { data, loading, reload } = useFetch("/admin/users");
  const [q, setQ] = useState(""); const [sel, setSel] = useState(null);
  const rows = useMemo(() => (data?.users || []).filter((u) => `${u.email} ${u.name} ${u.personA} ${u.personB}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const open = async (u) => { try { const r = await api.get(`/admin/users/${u.id}`); setSel(r.data); } catch (e) { toast.error(errMsg(e, "Gagal memuat detail")); } };
  return (
    <div className="space-y-4">
      <Card title={`Pengguna terdaftar (${data?.users?.length || 0})`} testId="users-card"
        action={<div className="flex items-center gap-2"><div className="flex items-center gap-1.5 rounded-full border border-[#eee5da] bg-[#fcfaf7] px-3 py-1.5"><Search className="h-3.5 w-3.5 text-[#a69995]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari email / nama" data-testid="users-search" className="w-36 bg-transparent text-[12px] outline-none" /></div><button onClick={reload} className="rounded-full bg-[#f1ece6] p-2" title="Muat ulang"><RefreshCw className="h-3.5 w-3.5" /></button></div>}>
        {loading ? <div className="text-[#a69995]">Memuat…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12.5px]" data-testid="users-table">
              <thead className="text-[11px] uppercase tracking-wide text-[#a69995]"><tr><th className="py-2 pr-3">Pasangan</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">Poin</th><th className="py-2 pr-3">Level</th><th className="py-2 pr-3">Booking</th><th className="py-2 pr-3">Daftar</th><th></th></tr></thead>
              <tbody className="divide-y divide-[#f1ece6]">
                {rows.map((u) => (
                  <tr key={u.id} className="hover:bg-[#fcfaf7]" data-testid="user-row">
                    <td className="py-2.5 pr-3 font-semibold">{u.name}<div className="text-[11px] font-normal text-[#a69995]">{u.personA} & {u.personB} · {u.mochiName}</div></td>
                    <td className="py-2.5 pr-3">{u.email}</td>
                    <td className="py-2.5 pr-3"><Badge tone={u.role === "admin" ? "red" : u.role === "demo" ? "gold" : "gray"}>{u.role}</Badge></td>
                    <td className="py-2.5 pr-3">{u.points.toLocaleString("id-ID")}</td>
                    <td className="py-2.5 pr-3">{u.level}</td>
                    <td className="py-2.5 pr-3">{u.completed}/{u.bookings}</td>
                    <td className="py-2.5 pr-3 text-[#6e594c]">{fmtDate(u.createdAt)}</td>
                    <td className="py-2.5"><button onClick={() => open(u)} data-testid="user-detail-btn" className="rounded-full bg-[#1f1b18] px-3 py-1 text-[11px] font-bold text-white">Detail</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {sel && (
        <Card title={`Detail · ${sel.user.name}`} testId="user-detail" action={<button onClick={() => setSel(null)} className="text-[12px] font-bold text-[#ff5a5f]">Tutup</button>}>
          <div className="grid gap-3 text-[13px] md:grid-cols-3">
            <div><b>Email:</b> {sel.user.email}</div><div><b>Poin:</b> {sel.user.points}</div><div><b>Mochi:</b> {sel.user.mochiName} · Lv {sel.user.level} · {sel.user.alive ? "hidup" : "pingsan"}</div>
            <div><b>Anniversary:</b> {sel.state.anniversary || "—"}</div><div><b>Kenangan:</b> {sel.user.memories}</div><div><b>Lencana:</b> {(sel.user.badges || []).join(", ") || "—"}</div>
          </div>
          <h3 className="mb-2 mt-4 text-[13px] font-bold">Riwayat booking</h3>
          <ul className="divide-y divide-[#f1ece6] text-[12.5px]">
            {(sel.state.bookings || []).map((b) => <li key={b.id} className="flex justify-between py-2"><span>{b.destName} · {b.date} {b.time}</span><span className="flex items-center gap-2">{fmtIDR(b.total)} <Badge tone={b.status === "completed" ? "green" : b.status === "cancelled" ? "red" : "blue"}>{b.status}</Badge></span></li>)}
            {!(sel.state.bookings || []).length && <li className="py-2 text-[#a69995]">Belum ada booking.</li>}
          </ul>
          <details className="mt-3"><summary className="cursor-pointer text-[12px] font-bold text-[#6e594c]">Lihat state mentah (JSON)</summary><pre className="mt-2 max-h-72 overflow-auto rounded-2xl bg-[#1f1b18] p-3 text-[11px] text-[#e8e1d8]">{JSON.stringify(sel.state, null, 2)}</pre></details>
        </Card>
      )}
    </div>
  );
}

const EMPTY_VENUE = { name: "", city: "Jakarta", area: "", category: "Restaurant", price: 150000, rating: 4.5, img: "", desc: "", amenities: "", moods: "", budget: "sedang", setting: "indoor" };
const CATS = ["Museum", "Crafting", "Restaurant", "Wisata Alam", "Rides"];

function VenueForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const I = (k, ph, type = "text", testId) => <input value={f[k]} onChange={set(k)} placeholder={ph} type={type} data-testid={testId || `venue-${k}`} className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12.5px] outline-none focus:border-[#ff5a5f]" />;
  return (
    <div className="rounded-2xl bg-[#fbf8f4] p-4" data-testid="venue-form">
      <div className="grid gap-2 md:grid-cols-3">
        {I("name", "Nama venue")}
        <select value={f.city} onChange={set("city")} data-testid="venue-city" className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12.5px]">{CITIES.map((c) => <option key={c}>{c}</option>)}</select>
        {I("area", "Area / kecamatan")}
        <select value={f.category} onChange={set("category")} data-testid="venue-category" className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12.5px]">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        {I("price", "Harga (Rp)", "number")}
        {I("rating", "Rating 0–5", "number")}
        {I("img", "URL gambar (boleh dari tab Aset)")}
        <select value={f.budget} onChange={set("budget")} className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12.5px]">{["hemat", "sedang", "mewah"].map((c) => <option key={c}>{c}</option>)}</select>
        <select value={f.setting} onChange={set("setting")} className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12.5px]">{["indoor", "outdoor"].map((c) => <option key={c}>{c}</option>)}</select>
        {I("amenities", "Fasilitas (pisahkan koma)")}
        {I("moods", "Mood (pisahkan koma, mis. tenang, kreatif)")}
        <textarea value={f.desc} onChange={set("desc")} placeholder="Deskripsi singkat" data-testid="venue-desc" className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12.5px] outline-none md:col-span-3" rows={2} />
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={() => onSave(f)} disabled={saving} data-testid="venue-save" className="rounded-2xl kk-coral-gradient px-4 py-2 text-[13px] font-bold text-white disabled:opacity-60">{saving ? "Menyimpan…" : "Simpan venue"}</button>
        <button onClick={onCancel} data-testid="venue-cancel" className="rounded-2xl bg-[#f1ece6] px-4 py-2 text-[13px] font-bold text-[#6e594c]">Batal</button>
      </div>
    </div>
  );
}

const toPayload = (f) => ({
  ...f, price: Number(f.price) || 0, rating: Number(f.rating) || 0,
  amenities: typeof f.amenities === "string" ? f.amenities.split(",").map((x) => x.trim()).filter(Boolean) : f.amenities,
  moods: typeof f.moods === "string" ? f.moods.split(",").map((x) => x.trim()).filter(Boolean) : f.moods,
});
const toForm = (d) => ({ ...EMPTY_VENUE, ...d, amenities: (d.amenities || []).join(", "), moods: (d.moods || []).join(", ") });

function VenuesTab() {
  const { data, loading } = useFetch("/admin/venue-stats");
  const adminVenues = useFetch("/admin/venues");
  const [city, setCity] = useState("Semua");
  const [editing, setEditing] = useState(null); // null | "new" | venue object
  const [saving, setSaving] = useState(false);
  const stats = data?.stats || {};
  // gabungkan venue bawaan + venue/override admin
  const rows = useMemo(() => {
    const ov = {}; const custom = [];
    (adminVenues.data?.venues || []).forEach((v) => { if (v.source === "override") ov[v.id] = v; else if (!v.deleted) custom.push({ ...v, source: "admin" }); });
    const base = DESTINATIONS.map((d) => ({ ...d, ...(ov[d.id] || {}), source: ov[d.id] ? "override" : "static" }));
    const ids = new Set(base.map((b) => b.id));
    return [...custom.filter((c) => !ids.has(c.id)), ...base].filter((d) => city === "Semua" || d.city === city);
  }, [adminVenues.data, city]);

  const save = async (f) => {
    setSaving(true);
    try {
      if (editing === "new") { await api.post("/admin/venues", toPayload(f)); toast.success("Venue ditambahkan"); }
      else { await api.put(`/admin/venues/${editing.id}`, toPayload(f)); toast.success("Venue diperbarui"); }
      setEditing(null); adminVenues.reload();
    } catch (e) { toast.error(errMsg(e, "Gagal menyimpan venue")); } finally { setSaving(false); }
  };
  const toggleActive = async (d) => {
    try { await api.put(`/admin/venues/${d.id}`, { active: d.active === false }); toast.success(d.active === false ? "Venue diaktifkan" : "Venue dinonaktifkan"); adminVenues.reload(); }
    catch (e) { toast.error(errMsg(e, "Gagal mengubah status")); }
  };
  const remove = async (d) => {
    if (!window.confirm(d.source === "admin" ? `Hapus venue "${d.name}"?` : `Kembalikan "${d.name}" ke data bawaan?`)) return;
    try { await api.delete(`/admin/venues/${d.id}`); toast.success("Tersimpan"); adminVenues.reload(); } catch (e) { toast.error(errMsg(e, "Gagal")); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Total venue" value={rows.length} testId="stat-venues" />
        <Stat label="Nonaktif" value={rows.filter((r) => r.active === false).length} testId="stat-inactive" />
        <Stat label="Buatan admin" value={rows.filter((r) => r.source === "admin").length} testId="stat-admin-venues" />
        <Stat label="Venue pernah dibooking" value={Object.values(stats).filter((s) => s.bookings > 0).length} />
      </div>
      <Card title="Daftar venue & tempat" testId="venues-card" action={
        <div className="flex items-center gap-2">
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="venues-city-filter" className="rounded-full border border-[#eee5da] bg-[#fcfaf7] px-3 py-1.5 text-[12px] font-semibold outline-none">{["Semua", ...CITIES].map((c) => <option key={c}>{c}</option>)}</select>
          <button onClick={() => setEditing("new")} data-testid="venue-add-btn" className="rounded-full kk-coral-gradient px-3.5 py-1.5 text-[12px] font-bold text-white">+ Tambah venue</button>
        </div>}>
        {editing && <div className="mb-4"><VenueForm key={editing === "new" ? "new" : editing.id} initial={editing === "new" ? EMPTY_VENUE : toForm(editing)} onSave={save} onCancel={() => setEditing(null)} saving={saving} /></div>}
        {loading ? <div className="text-[#a69995]">Memuat…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12.5px]" data-testid="venues-table">
              <thead className="text-[11px] uppercase tracking-wide text-[#a69995]"><tr><th className="py-2 pr-3">Venue</th><th className="py-2 pr-3">Kota / Area</th><th className="py-2 pr-3">Kategori</th><th className="py-2 pr-3">Harga</th><th className="py-2 pr-3">Booking</th><th className="py-2 pr-3">Pendapatan</th><th className="py-2 pr-3">Status</th><th></th></tr></thead>
              <tbody className="divide-y divide-[#f1ece6]">
                {rows.map((d) => { const s = stats[d.id] || {}; const off = d.active === false; return (
                  <tr key={d.id} className={`hover:bg-[#fcfaf7] ${off ? "opacity-60" : ""}`} data-testid="venue-row" data-venue-id={d.id}>
                    <td className="py-2.5 pr-3"><div className="flex items-center gap-2"><img src={d.img} alt="" className="h-9 w-12 rounded-lg bg-[#f1ece6] object-cover" /><div><div className="font-semibold">{d.name}</div>{d.source !== "static" && <Badge tone={d.source === "admin" ? "gold" : "blue"}>{d.source === "admin" ? "buatan admin" : "diubah"}</Badge>}</div></div></td>
                    <td className="py-2.5 pr-3">{d.city}<div className="text-[11px] text-[#a69995]">{d.area}</div></td>
                    <td className="py-2.5 pr-3"><Badge tone="blue">{d.category}</Badge></td>
                    <td className="py-2.5 pr-3">{fmtIDR(d.price)}</td>
                    <td className="py-2.5 pr-3">{s.completed || 0}/{s.bookings || 0}</td><td className="py-2.5 pr-3">{fmtIDR(s.revenue)}</td>
                    <td className="py-2.5 pr-3"><Badge tone={off ? "red" : "green"}>{off ? "nonaktif" : "aktif"}</Badge></td>
                    <td className="py-2.5"><div className="flex gap-1">
                      <button onClick={() => setEditing(d)} data-testid="venue-edit" className="rounded-full bg-[#1f1b18] px-2.5 py-1 text-[11px] font-bold text-white">Ubah</button>
                      <button onClick={() => toggleActive(d)} data-testid="venue-toggle" className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${off ? "bg-[#e3f5ea] text-[#1f7a4a]" : "bg-[#ffe3e3] text-[#c0343a]"}`}>{off ? "Aktifkan" : "Nonaktifkan"}</button>
                      {d.source !== "static" && <button onClick={() => remove(d)} data-testid="venue-delete" className="rounded-full bg-[#f1ece6] px-2 py-1 text-[11px] font-bold text-[#6e594c]" title={d.source === "admin" ? "Hapus" : "Kembalikan ke bawaan"}><Trash2 className="h-3 w-3" /></button>}
                    </div></td>
                  </tr>); })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[11px] text-[#a69995]">Perubahan langsung dipakai aplikasi pasangan saat dimuat ulang. Venue nonaktif disembunyikan dari Jelajah.</p>
      </Card>
    </div>
  );
}

function ActivitiesTab() {
  const { data, loading } = useFetch("/admin/activities");
  const [type, setType] = useState("semua");
  const rows = (data?.activities || []).filter((a) => type === "semua" || a.type === type);
  return (
    <Card title={`Riwayat kegiatan (${data?.total || 0})`} testId="activities-card" action={
      <div className="flex gap-1">{["semua", "booking", "points", "memory", "holiday"].map((t) => <button key={t} onClick={() => setType(t)} data-testid={`act-filter-${t}`} className={`rounded-full px-3 py-1 text-[11px] font-bold ${type === t ? "bg-[#1f1b18] text-white" : "bg-[#f1ece6] text-[#6e594c]"}`}>{t}</button>)}</div>}>
      {loading ? <div className="text-[#a69995]">Memuat…</div> : (
        <ul className="divide-y divide-[#f1ece6]" data-testid="activities-list">
          {rows.map((a, i) => (
            <li key={i} className="flex items-center justify-between gap-3 py-2.5 text-[13px]">
              <div className="min-w-0"><div className="truncate font-semibold">{a.label}</div><div className="text-[11px] text-[#a69995]">{a.couple} · {fmtDate(a.at)}{a.date ? ` · tgl kencan ${a.date}` : ""}</div></div>
              <div className="flex items-center gap-2">{a.amount != null && <span className="text-[12px] font-bold">{a.type === "booking" ? fmtIDR(a.amount) : `${a.status === "debit" ? "-" : "+"}${a.amount} poin`}</span>}<Badge tone={a.type === "booking" ? "blue" : a.type === "memory" ? "gold" : a.status === "credit" ? "green" : "gray"}>{a.type}</Badge></div>
            </li>
          ))}
          {!rows.length && <li className="py-3 text-[#a69995]">Belum ada aktivitas.</li>}
        </ul>
      )}
    </Card>
  );
}

const CHUNK = 2 * 1024 * 1024;
function AssetsTab() {
  const { data, loading, reload } = useFetch("/admin/assets");
  const [file, setFile] = useState(null); const [label, setLabel] = useState(""); const [category, setCategory] = useState("gambar");
  const [progress, setProgress] = useState(null); const inputRef = useRef();
  const upload = async () => {
    if (!file) return toast.error("Pilih file dulu");
    const uploadId = crypto.randomUUID(); const total = Math.ceil(file.size / CHUNK);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData(); fd.append("uploadId", uploadId); fd.append("index", i); fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        await api.post("/admin/assets/chunk", fd); setProgress(Math.round(((i + 1) / total) * 90));
      }
      const fd = new FormData(); fd.append("uploadId", uploadId); fd.append("filename", file.name); fd.append("total", total); fd.append("category", category); fd.append("label", label || file.name);
      await api.post("/admin/assets/complete", fd); setProgress(100);
      toast.success("Aset terunggah ke penyimpanan"); setFile(null); setLabel(""); if (inputRef.current) inputRef.current.value = ""; reload();
    } catch (e) { toast.error(errMsg(e, "Upload gagal")); } finally { setTimeout(() => setProgress(null), 800); }
  };
  const remove = async (a) => { if (!window.confirm(`Hapus aset "${a.label}"?`)) return; try { await api.delete(`/admin/assets/${a.id}`); toast.success("Aset dihapus"); reload(); } catch (e) { toast.error(errMsg(e, "Gagal menghapus")); } };
  return (
    <div className="space-y-4">
      <Card title="Unggah aset baru" testId="asset-upload-card">
        <div className="grid gap-3 md:grid-cols-[1fr_180px_160px_auto]">
          <input ref={inputRef} type="file" data-testid="asset-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-2xl border border-dashed border-[#e0d5c8] bg-[#fcfaf7] px-3 py-2 text-[12px]" />
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (opsional)" data-testid="asset-label-input" className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12px] outline-none" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="asset-category" className="rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2 text-[12px] outline-none">{["gambar", "model-3d", "suara", "video", "dokumen", "umum"].map((c) => <option key={c}>{c}</option>)}</select>
          <button onClick={upload} disabled={progress !== null} data-testid="asset-upload-btn" className="flex items-center justify-center gap-2 rounded-2xl kk-coral-gradient px-4 py-2 text-[13px] font-bold text-white disabled:opacity-60"><Upload className="h-4 w-4" /> Unggah</button>
        </div>
        {progress !== null && <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#f1ece6]"><div className="h-full kk-coral-gradient transition-all" style={{ width: `${progress}%` }} data-testid="asset-progress" /></div>}
        <p className="mt-2 text-[11px] text-[#a69995]">Mendukung gambar, model 3D (.glb), suara, video, dokumen · maks 80MB · diunggah bertahap (chunk 2MB) ke Emergent Object Storage.</p>
      </Card>
      <Card title={`Aset tersimpan (${data?.assets?.length || 0})`} testId="assets-card">
        {loading ? <div className="text-[#a69995]">Memuat…</div> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="assets-grid">
            {(data?.assets || []).map((a) => (
              <div key={a.id} className="rounded-2xl border border-[#f1ece6] p-3" data-testid="asset-item">
                <div className="mb-2 grid h-28 place-items-center overflow-hidden rounded-xl bg-[#fbf8f4]">
                  {a.content_type?.startsWith("image/") ? <img src={`${API}${a.url}`} alt={a.label} className="h-full w-full object-cover" /> : a.content_type?.startsWith("audio/") ? <audio controls src={`${API}${a.url}`} className="w-11/12" /> : <span className="text-[11px] font-bold uppercase text-[#a69995]">{a.content_type}</span>}
                </div>
                <div className="truncate text-[13px] font-semibold">{a.label}</div>
                <div className="text-[11px] text-[#a69995]">{a.original_filename} · {fmtSize(a.size)} · <Badge>{a.category}</Badge></div>
                <div className="mt-2 flex items-center justify-between">
                  <a href={`${API}${a.url}`} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-[#2b5fb3]">Buka URL</a>
                  <button onClick={() => remove(a)} data-testid="asset-delete" className="flex items-center gap-1 text-[11px] font-bold text-[#c0343a]"><Trash2 className="h-3.5 w-3.5" /> Hapus</button>
                </div>
              </div>
            ))}
            {!(data?.assets || []).length && <div className="text-[#a69995]">Belum ada aset.</div>}
          </div>
        )}
      </Card>
    </div>
  );
}

function SettingsTab() {
  const { data, loading, reload } = useFetch("/admin/settings");
  const [phone, setPhone] = useState(""); const [code, setCode] = useState("");
  const [step, setStep] = useState("idle"); const [busy, setBusy] = useState(false); const [sentTo, setSentTo] = useState("");
  const [notify, setNotify] = useState("");
  useEffect(() => { if (data) { setPhone(data.phone || ""); setNotify(data.notifyEmail || ""); } }, [data]);
  const saveNotify = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", { notifyEmail: notify }); toast.success("Email penerima kode disimpan"); reload(); }
    catch (e) { toast.error(errMsg(e, "Gagal menyimpan email")); } finally { setBusy(false); }
  };
  const request = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/settings/phone/request", { phone }); setSentTo(r.data.sentTo); setStep("code"); toast.success(`Kode verifikasi dikirim ke ${r.data.sentTo}`); }
    catch (e) { toast.error(errMsg(e, "Gagal mengirim kode")); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post("/admin/settings/phone/verify", { code }); toast.success("Nomor HP admin terverifikasi & tersimpan"); setStep("idle"); setCode(""); reload(); }
    catch (e) { toast.error(errMsg(e, "Kode salah")); } finally { setBusy(false); }
  };
  if (loading || !data) return <div className="p-6 text-[#a69995]">Memuat…</div>;
  return (
    <Card title="Pengaturan admin" testId="settings-card">
      <div className="grid gap-4 md:grid-cols-2">
        <div><label className="text-[12px] font-bold text-[#6e594c]">Email login admin</label><div className="mt-1 rounded-2xl bg-[#fbf8f4] px-3 py-2.5 text-[13px]" data-testid="settings-email">{data.email}</div>
          <label className="mt-3 block text-[12px] font-bold text-[#6e594c]">Email penerima kode verifikasi</label>
          <div className="mt-1 flex gap-2">
            <input value={notify} onChange={(e) => setNotify(e.target.value)} data-testid="settings-notify-email" className="flex-1 rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2.5 text-[13px] outline-none focus:border-[#ff5a5f]" placeholder="email aktif Anda" />
            <button onClick={saveNotify} disabled={busy || notify === data.notifyEmail} data-testid="settings-notify-save" className="rounded-2xl bg-[#1f1b18] px-4 text-[13px] font-bold text-white disabled:opacity-60">Simpan</button>
          </div>
          <p className="mt-1.5 text-[11px] text-[#a69995]">Kode 6 digit untuk ganti nomor HP dikirim ke alamat ini — pastikan kotak masuknya aktif.</p></div>
        <div>
          <label className="text-[12px] font-bold text-[#6e594c]">Nomor HP admin {data.phoneVerified ? <Badge tone="green">terverifikasi</Badge> : <Badge>belum diverifikasi</Badge>}</label>
          <div className="mt-1 flex gap-2">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={step === "code"} data-testid="settings-phone" className="flex-1 rounded-2xl border border-[#eee5da] bg-[#fcfaf7] px-3 py-2.5 text-[13px] outline-none focus:border-[#ff5a5f] disabled:opacity-60" placeholder="+62…" />
            <button onClick={request} disabled={busy || step === "code" || phone === data.phone} data-testid="settings-send-code" className="rounded-2xl kk-coral-gradient px-4 text-[13px] font-bold text-white disabled:opacity-60">Kirim kode</button>
          </div>
          {step === "code" && (
            <div className="mt-3 rounded-2xl bg-[#fff7e8] p-3" data-testid="settings-code-box">
              <p className="mb-2 text-[12px] text-[#6e594c]">Masukkan 6 digit kode yang dikirim ke <b>{sentTo}</b> (berlaku 10 menit).</p>
              <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" data-testid="settings-code" className="w-40 rounded-2xl border border-[#eee5da] bg-white px-3 py-2.5 text-center text-[16px] font-extrabold tracking-[6px] outline-none" placeholder="••••••" />
                <button onClick={verify} disabled={busy || code.length !== 6} data-testid="settings-verify" className="rounded-2xl bg-[#1f1b18] px-4 text-[13px] font-bold text-white disabled:opacity-60">Verifikasi</button>
                <button onClick={() => { setStep("idle"); setPhone(data.phone || ""); }} data-testid="settings-cancel-code" className="rounded-2xl bg-[#f1ece6] px-3 text-[12px] font-bold text-[#6e594c]">Batal</button>
                <button onClick={request} disabled={busy} className="text-[12px] font-bold text-[#ff5a5f]">Kirim ulang</button>
              </div>
            </div>
          )}
          <p className="mt-1.5 text-[11px] text-[#a69995]">Nomor baru hanya tersimpan setelah kode dari email admin diverifikasi.</p>
        </div>
      </div>
    </Card>
  );
}
